import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  setDoc,
  query,
  where,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { fightService } from './fightService';
import { imageService } from './imageService';

export const eventService = {
  // Create a new event
  async createEvent(eventData) {
    const docRef = await addDoc(collection(db, 'events'), {
      ...eventData,
      createdAt: new Date()
    });
    return docRef.id;
  },

  // Update event with cover image
  async updateEventCoverImage(eventId, imageFile) {
    try {
      // Upload the image
      const coverImageUrl = await imageService.uploadEventCoverImage(eventId, imageFile);

      // Update the event document
      const docRef = doc(db, 'events', eventId);
      await updateDoc(docRef, { coverImage: coverImageUrl });

      return coverImageUrl;
    } catch (error) {
      console.error('Error updating event cover image:', error);
      throw new Error('Failed to update cover image');
    }
  },

  // Remove event cover image
  async removeEventCoverImage(eventId, currentImageUrl) {
    try {
      // Delete the image from storage
      if (currentImageUrl) {
        await imageService.deleteImage(currentImageUrl);
      }

      // Update the event document
      const docRef = doc(db, 'events', eventId);
      await updateDoc(docRef, { coverImage: null });
    } catch (error) {
      console.error('Error removing event cover image:', error);
      throw new Error('Failed to remove cover image');
    }
  },


  // Get event by slug (using slugKey for routing)
  // Note: Returns basic event data - use DataStore.getEnrichedEvent() to get fights
  async getEventBySlug(slug) {
    const slugKey = slug.toLowerCase(); // Ensure lowercase for lookup
    const q = query(
      collection(db, 'events'),
      where('slugKey', '==', slugKey)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0];
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        // Convert Firestore Timestamp to JavaScript Date
        date: data.date?.toDate ? data.date.toDate() : data.date,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
      };
    }
    return null;
  },

  // Check if slug is available (using slugKey)
  async isSlugAvailable(slugKey) {
    const q = query(
      collection(db, 'events'),
      where('slugKey', '==', slugKey.toLowerCase())
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.empty;
  },

  // Update event
  async updateEvent(eventId, eventData) {
    const docRef = doc(db, 'events', eventId);
    await updateDoc(docRef, eventData);
  },

  // Delete event and all related data
  async deleteEvent(eventId) {
    // Delete all votes for this event
    const votesQuery = query(
      collection(db, 'votes'),
      where('eventId', '==', eventId)
    );
    const votesSnapshot = await getDocs(votesQuery);
    const voteDeletePromises = votesSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(voteDeletePromises);

    // Delete all fights for this event
    const fightsQuery = query(
      collection(db, 'fights'),
      where('eventId', '==', eventId)
    );
    const fightsSnapshot = await getDocs(fightsQuery);
    const fightDeletePromises = fightsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(fightDeletePromises);

    // Finally, delete the event itself
    const docRef = doc(db, 'events', eventId);
    await deleteDoc(docRef);
  },

  // Add fight to event (delegates to fightService)
  async addFight(eventId, fight, dataStore = null) {
    return await fightService.addFightToEvent(eventId, fight, dataStore);
  },

  // Update fight status (delegates to fightService)
  async updateFightStatus(eventId, fightId, status, currentRound) {
    return await fightService.updateFightStatus(fightId, status, currentRound);
  },

};

export const voteService = {
  // Generate deterministic vote document ID to prevent duplicates
  generateVoteId(userId, fightId, type, round = null) {
    if (round) {
      // For round votes, the round number implies it's a round_winner vote
      return `${userId}_${fightId}_round_${round}`;
    } else {
      // For fight votes, include the type (fight_winner)
      return `${userId}_${fightId}_${type}`;
    }
  },

  // Cast a vote with duplicate prevention
  async castVote(vote) {
    // Generate deterministic document ID
    const voteId = this.generateVoteId(vote.userId, vote.fightId, vote.type, vote.round);

    // Prepare vote data, filtering out undefined values
    const voteData = {
      eventId: vote.eventId,
      fightId: vote.fightId,
      userId: vote.userId,
      fighterId: vote.fighterId,
      type: vote.type,
      createdAt: new Date()
    };

    // Only add round field if it's not undefined
    if (vote.round !== undefined) {
      voteData.round = vote.round;
    }

    try {
      // Use setDoc with the deterministic ID - this will overwrite any existing vote
      // This prevents duplicates at the database level
      await setDoc(doc(db, 'votes', voteId), voteData);
    } catch (error) {
      // If there's a permission error, it might be due to rapid duplicate submissions
      // Try to check if a vote already exists and handle gracefully
      if (error.code === 'permission-denied') {
        const existingVoteRef = doc(db, 'votes', voteId);
        const existingVote = await getDoc(existingVoteRef);

        if (existingVote.exists()) {
          // Vote already exists, this is likely a duplicate submission
          console.log('Vote already exists, ignoring duplicate submission');
          return;
        }
      }

      // Re-throw other errors
      throw error;
    }
  },


};
